import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, FormGroup, InputWithUnit, Toggle } from '../ui/Card';
import { fmtDollar, fmtDollarInt } from '../../lib/format';
import { calculateAll } from '../../lib/calculator';
import { getCashIndicator, getPaymentIndicator } from '../../lib/price-levels';
import type { Offer, OfferContingencies, OfferStatus, EscalationClause, SellerTerms, CalculatorInputs, CalculatorResults } from '../../lib/types';

export interface UpdateData {
  earnestMoney: number;
  closingDate: string;
  status: OfferStatus;
  contingencies: OfferContingencies;
  notes: string;
  escalation: EscalationClause;
  sellerTerms: SellerTerms;
  // Updated inputs/results when concession changes
  inputs: CalculatorInputs;
  results: CalculatorResults;
}

interface Props {
  offer: Offer;
  cashOnHand: number;
  analysisPath: string;
  onSave: (data: UpdateData) => Promise<void>;
  onCancel?: () => void;
}

interface LadderRow {
  price: number;
  monthlyPayment: number;
  cashToClose: number;
  payIndicator: 'green' | 'yellow' | 'red';
  cashIndicator: 'green' | 'yellow' | 'red';
  isBase: boolean;
  isCap: boolean;
}

const STATUS_OPTIONS: { value: OfferStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

// MN deed tax: $3.30 per $1,000 of consideration (0.33%)
const MN_DEED_TAX_RATE = 0.0033;

function indicatorDot(indicator: 'green' | 'yellow' | 'red') {
  return <span className={`indicator indicator-${indicator}`} />;
}

function netRow(label: string, value: number, isMinus = false) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.2rem 0', fontSize: '0.85rem' }}>
      <span style={{ color: 'var(--text-muted)' }}>{isMinus ? '−' : ''} {label}</span>
      <span style={{ fontWeight: isMinus ? 400 : 600 }}>{isMinus ? `(${fmtDollarInt(value)})` : fmtDollarInt(value)}</span>
    </div>
  );
}

export function OfferPlanEditor({ offer, cashOnHand, analysisPath, onSave, onCancel }: Props) {
  const [earnestMoney, setEarnestMoney] = useState(offer.earnestMoney);
  const [closingDate, setClosingDate] = useState(offer.closingDate);
  const [status, setStatus] = useState<OfferStatus>(offer.status);
  const [contingencies, setContingencies] = useState<OfferContingencies>(offer.contingencies);
  const [notes, setNotes] = useState(offer.notes);
  const [escalation, setEscalation] = useState<EscalationClause>(
    offer.escalation ?? { enabled: false, increment: 2500, cap: 0 }
  );
  const [sellerConcession, setSellerConcession] = useState(offer.inputs.sellerConcession ?? 0);
  // Listing commission: persisted via sellerTerms so it survives page reloads
  const [listingCommissionPct, setListingCommissionPct] = useState(
    offer.sellerTerms?.listingCommissionPct ?? 2.5
  );

  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copied, setCopied] = useState(false);

  // Live inputs/results that reflect the current sellerConcession edit.
  // These are what gets persisted on save so the stored snapshot stays current.
  const liveInputs = useMemo((): CalculatorInputs => ({
    ...offer.inputs,
    sellerConcession,
  }), [offer.inputs, sellerConcession]);

  const liveResults = useMemo((): CalculatorResults => calculateAll(liveInputs), [liveInputs]);

  // Detect unsaved changes against the last-saved offer prop
  const hasChanges = useMemo(() => {
    return (
      sellerConcession !== (offer.inputs.sellerConcession ?? 0) ||
      listingCommissionPct !== (offer.sellerTerms?.listingCommissionPct ?? 2.5) ||
      earnestMoney !== offer.earnestMoney ||
      closingDate !== offer.closingDate ||
      status !== offer.status ||
      notes !== offer.notes ||
      JSON.stringify(contingencies) !== JSON.stringify(offer.contingencies) ||
      JSON.stringify(escalation) !== JSON.stringify(offer.escalation ?? { enabled: false, increment: 2500, cap: 0 })
    );
  }, [sellerConcession, listingCommissionPct, earnestMoney, closingDate, status, notes, contingencies, escalation, offer]);

  // Auto-save 3 s after the last change — mirrors the Calculator auto-save pattern
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasChanges) return;
    const sellerTerms: SellerTerms = { listingCommissionPct };
    const t = setTimeout(() => {
      onSave({ earnestMoney, closingDate, status, contingencies, notes, escalation, sellerTerms, inputs: liveInputs, results: liveResults })
        .then(() => setSavedFlash(true))
        .catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnestMoney, closingDate, status, notes, contingencies, escalation, sellerConcession, listingCommissionPct, hasChanges]);

  // Clear flash after 3 seconds
  useEffect(() => {
    if (!savedFlash) return;
    const t = setTimeout(() => setSavedFlash(false), 3000);
    return () => clearTimeout(t);
  }, [savedFlash]);

  const updateContingency = (key: keyof OfferContingencies, value: boolean) => {
    setContingencies(prev => ({ ...prev, [key]: value }));
  };

  const updateEscalation = <K extends keyof EscalationClause>(key: K, value: EscalationClause[K]) => {
    setEscalation(prev => ({ ...prev, [key]: value }));
  };

  const grossMonthly = liveInputs.grossAnnualIncome / 12;

  // Live indicators from recalculated results
  const livePayIndicator = getPaymentIndicator(liveResults.totalMonthly, grossMonthly);
  const liveCashIndicator = getCashIndicator(liveResults.cashToClose.cashToClose, cashOnHand);

  // Build the escalation ladder using live inputs (so concession changes reflect in ladder C2C)
  const ladder = useMemo((): LadderRow[] => {
    if (!escalation.enabled || escalation.cap <= offer.offerPrice || escalation.increment <= 0) {
      return [];
    }
    const rows: LadderRow[] = [];
    let price = offer.offerPrice;
    while (rows.length < 25) {
      const effectivePrice = Math.min(price, escalation.cap);
      const inputs = {
        ...liveInputs,
        homePrice: effectivePrice,
        downPaymentAmt: Math.round(effectivePrice * (liveInputs.downPaymentPct / 100)),
      };
      const results = calculateAll(inputs);
      rows.push({
        price: effectivePrice,
        monthlyPayment: results.totalMonthly,
        cashToClose: results.cashToClose.cashToClose,
        payIndicator: getPaymentIndicator(results.totalMonthly, grossMonthly),
        cashIndicator: getCashIndicator(results.cashToClose.cashToClose, cashOnHand),
        isBase: rows.length === 0,
        isCap: effectivePrice >= escalation.cap,
      });
      if (effectivePrice >= escalation.cap) break;
      price += escalation.increment;
    }
    return rows;
  }, [escalation, offer.offerPrice, liveInputs, cashOnHand, grossMonthly]);

  // First row where the payment is no longer green — used for the tip banner
  const stretchRow = ladder.find(r => r.payIndicator !== 'green');

  // Seller's net proceeds estimate
  const listingCommissionAmt = Math.round(offer.offerPrice * listingCommissionPct / 100);
  const mnDeedTax = Math.round(offer.offerPrice * MN_DEED_TAX_RATE);
  const sellerNetProceeds = offer.offerPrice - listingCommissionAmt - sellerConcession - mnDeedTax;

  const handleSave = async () => {
    setSaving(true);
    const sellerTerms: SellerTerms = { listingCommissionPct };
    try {
      await onSave({ earnestMoney, closingDate, status, contingencies, notes, escalation, sellerTerms, inputs: liveInputs, results: liveResults });
      setSavedFlash(true);
    } catch (err: any) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Generate plain-text offer summary for sharing with broker / finance team
  const generateSummary = useCallback((): string => {
    const dp = liveInputs.downPaymentAmt;
    const dpPct = liveInputs.downPaymentPct;
    const loanAmt = liveResults.loanAmount;
    const loanTypeLabel = liveInputs.loanType === 'conventional' ? 'Conventional'
      : liveInputs.loanType.toUpperCase();
    const termLabel = `${liveInputs.loanTerm}yr ${loanTypeLabel}`;

    const activeContingencies = [
      contingencies.inspection      && 'Inspection',
      contingencies.appraisal       && 'Appraisal',
      contingencies.financing       && 'Financing',
      contingencies.saleDependency  && 'Sale of Current Home',
    ].filter(Boolean) as string[];
    const waived = [
      !contingencies.inspection     && 'Inspection',
      !contingencies.appraisal      && 'Appraisal',
      !contingencies.financing      && 'Financing',
      !contingencies.saleDependency && 'Sale of Current Home',
    ].filter(Boolean) as string[];

    const propLine = [liveInputs.propertyAddress, liveInputs.propertyCity].filter(Boolean).join(', ');

    // Estimated closing costs from inputs (actual breakdown lives in cashToClose.items)
    const estClosingCosts = Math.round(liveInputs.homePrice * liveInputs.closingCostPct / 100);

    const lines: (string | null)[] = [
      'OFFER SUMMARY',
      '═'.repeat(44),
      propLine ? `Property:       ${propLine}` : null,
      `Offer Price:    ${fmtDollarInt(offer.offerPrice)}`,
      `Down Payment:   ${fmtDollarInt(dp)} (${dpPct}%)`,
      `Loan Amount:    ${fmtDollarInt(loanAmt)} — ${termLabel}`,
      `Interest Rate:  ${liveInputs.interestRate}%`,
      '',
      'ESTIMATED MONTHLY PAYMENT',
      '─'.repeat(44),
      `  Principal & Interest:  ${fmtDollar(liveResults.monthlyPI)}`,
      `  Property Tax:          ${fmtDollar(liveResults.monthlyTax)}`,
      `  Home Insurance:        ${fmtDollar(liveResults.monthlyInsurance)}`,
      liveInputs.hoaFees > 0 ? `  HOA:                   ${fmtDollar(liveInputs.hoaFees)}` : null,
      liveResults.monthlyPMI > 0 ? `  PMI:                   ${fmtDollar(liveResults.monthlyPMI)}` : null,
      liveInputs.use401k ? `  401(k) Loan Repayment: ${fmtDollar(liveResults.monthly401k)}` : null,
      `  TOTAL:                 ${fmtDollar(liveResults.totalMonthly)}/mo`,
      '',
      'ESTIMATED CASH TO CLOSE',
      '─'.repeat(44),
      `  Down Payment:          ${fmtDollarInt(dp)}`,
      `  Closing Costs (~${liveInputs.closingCostPct}%): ${fmtDollarInt(estClosingCosts)}`,
      sellerConcession > 0 ? `  Seller Concessions:    (${fmtDollarInt(sellerConcession)})` : null,
      earnestMoney > 0 ? `  Earnest Money (paid):  (${fmtDollarInt(earnestMoney)})` : null,
      `  TOTAL:                 ${fmtDollarInt(liveResults.cashToClose.cashToClose)}`,
      '',
      'OFFER TERMS',
      '─'.repeat(44),
      `  Earnest Money:         ${fmtDollarInt(earnestMoney)}`,
      closingDate ? `  Proposed Closing:      ${new Date(closingDate + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : null,
      sellerConcession > 0 ? `  Seller Concessions:    ${fmtDollarInt(sellerConcession)}` : null,
      '',
      'CONTINGENCIES',
      '─'.repeat(44),
      activeContingencies.length > 0 ? `  Included:  ${activeContingencies.join(', ')}` : null,
      waived.length > 0 ? `  Waived:    ${waived.join(', ')}` : null,
      escalation.enabled && escalation.cap > offer.offerPrice ? [
        '',
        'ESCALATION CLAUSE',
        '─'.repeat(44),
        `  Beats competing offers by ${fmtDollarInt(escalation.increment)}`,
        `  Maximum offer cap: ${fmtDollarInt(escalation.cap)}`,
      ].join('\n') : null,
      notes.trim() ? [
        '',
        'NOTES',
        '─'.repeat(44),
        `  ${notes.trim().replace(/\n/g, '\n  ')}`,
      ].join('\n') : null,
      '',
      '─'.repeat(44),
      `Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · Threshold Mortgage Tool`,
    ];

    return lines.filter(l => l !== null).join('\n');
  }, [offer.offerPrice, liveInputs, liveResults, earnestMoney, closingDate, sellerConcession, contingencies, escalation, notes]);

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(generateSummary());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: select a textarea
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' }}>

      {/* Auto-save status toast */}
      {(savedFlash || hasChanges) && (
        <div
          className="autosave-toast"
          style={{
            background: savedFlash ? 'rgba(34,197,94,0.15)' : 'rgba(217,119,6,0.1)',
            border: `1px solid ${savedFlash ? 'rgba(34,197,94,0.4)' : 'rgba(217,119,6,0.35)'}`,
            color: savedFlash ? '#22c55e' : '#d97706',
          }}
        >
          {savedFlash ? '✓ Auto-saved' : '● Modified'}
        </div>
      )}

      {/* Financial Snapshot */}
      <Card title="Financial Snapshot">
        <div style={{
          background: 'var(--section-bg)',
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <div className="snapshot-grid">
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{fmtDollarInt(offer.offerPrice)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Offer Price</div>
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                {indicatorDot(livePayIndicator)}
                {fmtDollar(liveResults.totalMonthly)}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Monthly Payment</div>
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                {indicatorDot(liveCashIndicator)}
                {fmtDollarInt(liveResults.cashToClose.cashToClose)}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Cash to Close</div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.25rem' }}>
          <span>Based on analysis saved {new Date(offer.createdAt).toLocaleString()}</span>
          <span style={{ display: 'flex', gap: '0.75rem' }}>
            <Link to={analysisPath} style={{ color: 'var(--primary)' }}>View →</Link>
            <Link to={`${analysisPath}?returnToOffer=${offer.id}`} style={{ color: 'var(--primary)' }}>Edit Analysis →</Link>
          </span>
        </div>
      </Card>

      {/* Escalation Clause */}
      <Card title="Escalation Clause">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          An escalation clause automatically beats competing offers by a set increment, up to your cap.
        </p>
        <Toggle
          label="Include escalation clause"
          checked={escalation.enabled}
          onChange={v => updateEscalation('enabled', v)}
        />

        {escalation.enabled && (
          <>
            <div className="form-row" style={{ marginTop: '0.75rem' }}>
              <FormGroup label="Beat by (increment)">
                <InputWithUnit
                  unit="$"
                  value={escalation.increment}
                  onChange={v => updateEscalation('increment', v)}
                  min={500}
                  step={500}
                />
              </FormGroup>
              <FormGroup label="Maximum offer (cap)">
                <InputWithUnit
                  unit="$"
                  value={escalation.cap || ''}
                  onChange={v => updateEscalation('cap', v)}
                  min={offer.offerPrice}
                  step={1000}
                />
              </FormGroup>
            </div>

            {/* Tip banner — fires at first non-green payment row */}
            {stretchRow && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '0.6rem 0.8rem',
                background: 'rgba(234,179,8,0.1)',
                border: '1px solid rgba(234,179,8,0.4)',
                borderRadius: '6px',
                fontSize: '0.82rem',
                margin: '0.75rem 0 0.5rem',
              }}>
                <span style={{ flexShrink: 0 }}>💡</span>
                <span>
                  Budget stretches at <strong>{fmtDollarInt(stretchRow.price)}</strong> —
                  payment becomes <strong>{fmtDollar(stretchRow.monthlyPayment)}/mo</strong>
                </span>
              </div>
            )}

            {/* Ladder table */}
            {ladder.length > 0 && (
              <div className="table-scroll-wrap" style={{ marginTop: '0.75rem' }}>
                <table className="price-levels-table">
                  <thead>
                    <tr>
                      <th>Offer Price</th>
                      <th>Monthly Payment</th>
                      <th>Cash to Close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ladder.map((row, i) => (
                      <tr key={i} className={row.isBase ? 'price-level-at-asking' : ''}>
                        <td>
                          <span style={{ fontWeight: row.isBase || row.isCap ? 700 : 400 }}>
                            {fmtDollarInt(row.price)}
                          </span>
                          {row.isBase && (
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.4rem' }}>base</span>
                          )}
                          {row.isCap && !row.isBase && (
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.4rem' }}>cap</span>
                          )}
                        </td>
                        <td>
                          {indicatorDot(row.payIndicator)}
                          {fmtDollar(row.monthlyPayment)}
                        </td>
                        <td>
                          {indicatorDot(row.cashIndicator)}
                          {fmtDollarInt(row.cashToClose)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Helpful message when cap isn't set yet */}
            {ladder.length === 0 && escalation.cap <= offer.offerPrice && escalation.cap > 0 && (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Cap must be higher than the offer price ({fmtDollarInt(offer.offerPrice)}).
              </p>
            )}
          </>
        )}
      </Card>

      {/* Offer Terms */}
      <Card title="Offer Terms">
        <div className="form-row">
          <FormGroup label="Earnest Money">
            <InputWithUnit
              unit="$"
              value={earnestMoney}
              onChange={setEarnestMoney}
              min={0}
              step={500}
            />
          </FormGroup>
          <FormGroup label="Proposed Closing Date">
            <input
              type="date"
              value={closingDate}
              onChange={e => setClosingDate(e.target.value)}
            />
          </FormGroup>
        </div>
        <div className="form-row">
          <FormGroup label="Seller Concessions">
            <InputWithUnit
              unit="$"
              value={sellerConcession}
              onChange={setSellerConcession}
              min={0}
              step={500}
            />
          </FormGroup>
          <FormGroup label="Status">
            <select
              value={status}
              onChange={e => setStatus(e.target.value as OfferStatus)}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FormGroup>
        </div>
        {sellerConcession > 0 && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            Concessions reduce your cash to close by {fmtDollarInt(sellerConcession)} — reflected in the snapshot above.
          </div>
        )}
      </Card>

      {/* Contingencies */}
      <Card title="Contingencies">
        <div className="contingency-grid">
          <Toggle
            label="Inspection"
            checked={contingencies.inspection}
            onChange={v => updateContingency('inspection', v)}
          />
          <Toggle
            label="Appraisal"
            checked={contingencies.appraisal}
            onChange={v => updateContingency('appraisal', v)}
          />
          <Toggle
            label="Financing"
            checked={contingencies.financing}
            onChange={v => updateContingency('financing', v)}
          />
          <Toggle
            label="Sale of Current Home"
            checked={contingencies.saleDependency}
            onChange={v => updateContingency('saleDependency', v)}
          />
        </div>
      </Card>

      {/* Seller's Perspective */}
      <Card title="Seller's Perspective">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          Estimate what the seller nets from this offer — useful for understanding how competitive it looks.
        </p>
        <FormGroup label="Listing agent commission (seller's side)">
          <InputWithUnit
            unit="%"
            unitPosition="right"
            value={listingCommissionPct}
            onChange={setListingCommissionPct}
            min={0}
            max={10}
            step={0.25}
          />
        </FormGroup>
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
          {netRow('Offer price', offer.offerPrice)}
          <div style={{ height: '1px', background: 'var(--border)', margin: '0.35rem 0' }} />
          {netRow('Listing agent commission', listingCommissionAmt, true)}
          {sellerConcession > 0 && netRow('Seller concessions', sellerConcession, true)}
          {netRow('MN deed tax (~0.33%)', mnDeedTax, true)}
          <div style={{ height: '2px', background: 'var(--border)', margin: '0.35rem 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.2rem 0' }}>
            <span style={{ fontWeight: 600 }}>Estimated net to seller</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)' }}>{fmtDollarInt(sellerNetProceeds)}</span>
          </div>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          Excludes seller's title insurance, prorated taxes, HOA transfer fees, and any outstanding liens. For estimation only.
        </div>
      </Card>

      {/* Notes */}
      <Card title="Notes & Additional Terms">
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Additional terms, conditions, or notes for your agent..."
          rows={4}
        />
        <div className="note-box" style={{ marginTop: '0.75rem' }}>
          This is a reference tool to help organize your thoughts before reaching out to your agent.
          It does not generate legal documents or submit offers externally.
        </div>
      </Card>

      {/* Offer Summary */}
      <Card title="Offer Summary">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          A plain-text summary you can share with your broker or finance team.
        </p>
        <pre style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.72rem',
          lineHeight: 1.65,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: 'var(--section-bg)',
          padding: '0.85rem 1rem',
          borderRadius: '8px',
          margin: '0 0 0.75rem',
          color: 'var(--text)',
        }}>
          {generateSummary()}
        </pre>
        <button
          className="btn btn-secondary"
          onClick={handleCopySummary}
          style={copied ? { background: '#dcfce7', borderColor: '#86efac', color: '#16a34a' } : undefined}
        >
          {copied ? '✓ Copied!' : 'Copy to Clipboard'}
        </button>
      </Card>

      {/* Save bar */}
      <div className="offer-save-bar">
        {onCancel && (
          <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={
            savedFlash
              ? { background: '#22c55e', borderColor: '#22c55e' }
              : !hasChanges
              ? { opacity: 0.5 }
              : undefined
          }
        >
          {saving ? 'Saving…' : savedFlash ? '✓ Saved' : 'Save Offer Plan'}
        </button>
      </div>
    </div>
  );
}
